<template>
  <div class="handover-information">
    <Sticky
      class="sticky-btn"
      :class="isTenantClass"
      extendStickyClass="stick-class"
      :fullWidth="false"
      align="right"
    >
      <div
        v-show="!isTenant"
        class="btnGroup"
      >
        <button
          v-show="isEdit && (allowEditDate || allowApprove)"
          class="btn--darkblue cancel-btn"
          @click="discardChanges"
        >
          {{ $t('action.cancel') }}
        </button>
        <button
          v-show="!allowEdit && !isEdit && (allowEditDate || allowApprove)"
          class="btn--orange edit-btn"
          @click="enableEdit"
        >
          {{ $t('action.edit') }}
        </button>
        <button
          v-show="(allowEdit || (isEdit && allowApprove))"
          :class="saveButtonClass"
          @click="save"
        >
          {{ $t('action.save') }}
        </button>
        <SubmitHandover
          v-show="(allowEdit || (isEdit && allowEditDate))"
          :options="approvers"
          :resetValidation="resetValidation"
          :validate="validate"
          :disabled="!allowEdit"
          @hotb:approve="approve"
        />
        <approval
          v-show="!isEdit && allowApprove"
          :id="'handover-reject'"
          class="handover-reject approval"
          :btnName="$t('action.reject')"
          :btnClass="'btn--darkblue'"
          :updateStatus="reject"
        />
        <button
          v-show="!isEdit && allowApprove"
          id="handover-approve"
          class="btn--orange approval"
          @click="approve"
        >
          {{ $t('action.approve') }}
        </button>
        <button
          v-show="!isEdit && allowInvite && !isSpecial"
          class="btn--orange"
          @click="showInviteModal()"
        >
          {{ $t('handover.arrangeNotify') }}
        </button>
      </div>
      <DialogSlot
        v-if="hkldHotbContacts.length && isTenant && !isCompleted"
        id="tenant-hotb-request-change"
        ref="fillRequestChange"
        class="item tenant-handover-request-change-btn"
        :modalBtn="$t('action.requestChange')"
        :modalBtnClass="'btn--darkblue'"
        :beforeCloseModal="() => beforeCloseRequestChangeModal()"
      >
        <div
          slot="content"
          class="request-change-comment"
        >
          <p class="font-bold">
            {{ $t('hint.changeHandover') }}
          </p>
          <TextField
            required
            :value="requestChangeComments"
            @updateValue="val => requestChangeComments = val"
          />
        </div>
        <div slot="buttons">
          <DialogSlot
            id="tenant-hotb-request-change-submitted"
            ref="requestChange"
            :modalBtn="$t('action.continue')"
            :modalBtnClass="'btn--orange'"
            :beforeOpenModal="requestChange"
          >
            <div
              slot="content"
              class="font-bold"
            >
              {{ $t('hint.changedRequested') }}
            </div>
            <div slot="buttons">
              <button
                class="btn--darkblue"
                @click="closeModal()"
              >
                {{ $t('action.ok') }}
              </button>
            </div>
          </DialogSlot>
        </div>
      </DialogSlot>
    </Sticky>
    <div
      v-if="!isTenant"
      class="basicInfo"
    >
      <div class="basicItem">
        <div
          class="title"
          :class="{'required-flag': allowEdit}"
        >
          {{ $t('company.basic.companyName') }}
        </div>
        <div class="value">
          <HKLInput
            v-if="allowEdit"
            :key="companyNameKey"
            :value="companyName"
            @blur="updateCompanyName"
          />
          <template v-else>
            {{ companyName }}
          </template>
        </div>
      </div>
      <div class="basicItem">
        <div
          class="title"
          :class="{'required-flag': allowEdit}"
        >
          {{ $t('tenancy.tradeName') }}
        </div>
        <div class="value">
          <HKLInput
            v-if="allowEdit"
            :key="tradeNameKey"
            :value="tradeName"
            @blur="updateTradeName"
          />
          <template v-else>
            {{ tradeName }}
          </template>
        </div>
      </div>
      <div class="basicItem building">
        <div class="title">
          {{ $t('tenancy.building') }}
        </div>
        <div class="value">
          {{ deep(currentHotb, 'tenancy.building.code') }}
        </div>
      </div>
      <div class="basicItem status">
        <div class="title">
          {{ $t('status.name') }}
        </div>
        <div
          class="value status-text"
          @click="openLifecycleModal"
        >
          {{ currentHotb.status | customStatusLabel }}
        </div>
      </div>
      <div class="basicItem lastUpdateOnandBy">
        <div class="title">
          {{ $t('handover.lastUpdateOnandBy') }}
        </div>
        <div class="value">
          {{ lastUpdatedBy }}<br>{{ dateUtil.formatDate(currentHotb.updatedAt, true, true) }}
        </div>
      </div>
    </div>
    <div :class="allowEdit ? 'general-info-edit' : 'general-info-view'">
      <div
        v-show="isTenant"
        class="handoverLastUpdateOn"
      >
        <div class="title">
          {{ $t('handover.lastUpdateOn') }}
        </div>
        <div class="value">
          {{ dateUtil.formatDate(currentHotb.infoUpdatedAt || currentHotb.updatedAt, true, true) }}
        </div>
      </div>
      <div
        v-show="isTenant"
        class="tenancyBuilding"
      >
        <div class="title">
          {{ $t('tenancy.building') }}
        </div>
        <div class="value">
          {{ deep(currentHotb, 'tenancy.building.fullName') }}
        </div>
      </div>
      <!-- Premises -->
      <div class="permisesName row">
        <TextField
          :key="premisesNameKey"
          :readonly="!allowEdit"
          :label="$t('handover.premises')"
          :showColon="false"
          :showFlag="allowEdit"
          :value="currentHotb.premises"
          :required="allowEdit"
          @updateValue="updatePremises"
        />
        <div class="floor">
          <label>{{ $t('tenancy.floor') }}</label>
          <HKLMultiSelect
            v-if="allowEdit"
            class="multi-value"
            :value="floor"
            :options="floorHklOptions"
            placeholder=""
            @change="onFloorChange"
          />
          <TextField
            v-else
            :readonly="true"
            :showColon="false"
            :showFlag="true"
            :value="showFloors()"
          />
        </div>
      </div>
      <!-- Handover Date -->
      <div
        v-if="!isTenant"
        class="date"
      >
        <div
          class="title"
          :class="{'required-flag': allowEdit || (isEdit && allowEditDate)}"
        >
          {{ isHandover ? $t('handover.date') : $t('takeback.date') }}
        </div>
        <DatePicker
          v-show="allowEdit || (isEdit && allowEditDate)"
          format="DD MMM YY"
          :disabledPicker="!allowEdit && !(isEdit && allowEditDate)"
          :calendarButton="true"
          :clearButton="allowEdit || (isEdit && allowEditDate)"
          :disabled="{}"
          :class="invalidDateErrHighlightCls"
          :value="dateUtil.format(currentHotb.transactionDate)"
          calendarButtonIcon="fa fa-calendar"
          placeholder=""
          @selected="val => updateField(['transactionDate'], val)"
        />
        <span v-show="!allowEdit && !(isEdit && allowEditDate)">{{ dateUtil.formatDate(currentHotb.transactionDate) }}</span>
      </div>
      <!-- scheduled handover date -->
      <div
        v-if="scheduledTransactionDate && isAfterScheduled"
        class="scheduled-handover-date"
      >
        <label>{{ $t('handover.scheduledTransactionDateAndTime') }}</label>
        <span>{{ formatDate(scheduledTransactionDate) }}</span>
      </div>
      <!-- Actual Handover Date -->
      <div
        v-if="currentHotb.completedDate"
        class="date"
      >
        <div class="title">
          {{ isHandover ? $t('handover.actualHandoverDate') : $t('handover.actualTakebackDate') }}
        </div>
        <span>{{ dateUtil.formatDate(currentHotb.completedDate) }}</span>
      </div>
      <!-- Hongkong Land Contact -->
      <div
        v-show="!isTenant"
        class="contact-info row"
      >
        <div
          v-if="allowEdit"
          class="handover-contact row"
        >
          <label :class="[{'required-flag': allowEdit}, 'width100']">{{ $t('handover.handoverContact') }}</label>
          <InputField
            v-for="fullnameObj of getFullnameSorts"
            :key="fullnameObj.key"
            class="small-6 medium-6 large-6"
            :placeholder="$t(`contact.basic.${fullnameObj.label}`)"
            :showColon="false"
            :showFlag="false"
            :value="deep(contacts, `name.${fullnameObj.value}`)"
            required
            @updateValue="val => updateContact(['name', fullnameObj.value], val)"
          />
        </div>
        <div
          v-else
          class="handover-contact"
        >
          <InputField
            :readonly="!allowEdit"
            :label="$t('handover.handoverContact')"
            :showColon="false"
            :showFlag="allowEdit"
            :value="getName() || '--'"
          />
        </div>
        <InputField
          class="phone-number"
          :readonly="!allowEdit"
          :label="$t('user.phone')"
          :showColon="false"
          :showFlag="allowEdit"
          :value="getContactValue(contacts, 'phone_number')"
          required
          @updateValue="val => updateContact(['phone_number'], val)"
        />
        <InputField
          class="email"
          :readonly="!allowEdit"
          :label="$t('user.email')"
          :showColon="false"
          :showFlag="allowEdit"
          :inputCheckType="'email'"
          :inputTip="$t('hint.inputTip.email')"
          :pattern="regex.email"
          :value="getContactValue(contacts, 'email')"
          required
          @updateValue="val => updateContact(['email'], val)"
        />
      </div>
      <!-- Handover contact -->
      <div
        v-show="hkldHotbContacts.length && isAfterScheduled"
        class="handover-contacts-info"
      >
        <template v-if="isTenant">
          <div class="item">
            <label>{{ $t('handover.handoverContact') }}</label>
            <span>{{ getName() || '--' }}</span>
          </div>
          <div class="item">
            <label>{{ $t('user.phone') }}</label>
            <span>{{ getContactValue(contacts, 'phone_number') || '--' }}</span>
          </div>
          <div class="item">
            <label>{{ $t('user.email') }}</label>
            <span>{{ getContactValue(contacts, 'email') }}</span>
          </div>
        </template>
        <div
          v-for="(item, index) in hkldHotbContacts"
          :key="index"
        >
          <div class="item">
            <label v-if="!index">{{ $t('handover.hkldContact') }}</label>
            <span>{{ getDisplayName(item) }}</span>
          </div>
          <div class="item">
            <label v-if="!index">{{ $t('user.phone') }}</label>
            <span>{{ getHkldUserInfo(item, 'PHONE_NUMBER') || '--' }}</span>
          </div>
          <div class="item">
            <label v-if="!index">{{ $t('user.email') }}</label>
            <span>{{ getHkldUserInfo(item, 'EMAIL') }}</span>
          </div>
        </div>
      </div>
      <!-- Remarks to Tenant -->
      <div
        v-show="hkldHotbContacts.length && isAfterScheduled"
        class="remark-to-tenant"
      >
        <label>{{ $t('handover.remarkToTenant') }}</label>
        <pre>{{ remarkToTenant || '--' }}</pre>
      </div>
      <div
        v-if="isTenant"
        class="attachments attach-to-tenant"
      >
        <div class="title">
          {{ $t('handover.attachmentsForTenant') }}
        </div>
        <EditAttachments
          :sourceId="pubDocResourceId"
          :canEdit="false"
          :getFileLengthID="pubDocResourceId"
          :canDelete="allowEditDocs"
          :canDownloadAll="false"
          :filesChanged="tenantFilesChanged"
          :updateDescriptionCb="tenantUpdateDescriptionCb "
        />
        <span v-if="isEmptyAttachmentsForTenant">--</span>
      </div>
      <hr v-if="!isTenant">
      <!-- For internal Only -->
      <!-- Condition of Premises -->
      <p
        v-if="!isTenant"
        class="internal-only"
      >
        {{ $t('handover.internalUseOnly') }}
      </p>
      <div
        v-if="!isTenant"
        class="premisesRemark"
      >
        <label
          class="emphasis"
          :class="{'required-flag': (allowEdit && isOthers)}"
        >
          {{ $t('handover.handoverSchedule.conditionOfPremises') }}
        </label>
        <div class="radio">
          <input
            v-show="allowEdit ? true : isAsIs"
            v-model="premiseBuildingType"
            type="radio"
            :disabled="!allowEdit"
            :value="hotbPremiseBuildingType.asIs"
            @change="updateField(['premiseBuildingType'], premiseBuildingType)"
          >
          <span
            v-show="allowEdit ? true : isAsIs"
            :class="{'en-bold': isAsIs}"
          >
            {{ hotbPremiseBuildingType.asIs | customValueByKey({obj: hotbPremiseBuildingType,rootPath: 'handover' }) }}
          </span>
          <input
            v-show="allowEdit ? true : isBareShell"
            v-model="premiseBuildingType"
            type="radio"
            :disabled="!allowEdit"
            :value="hotbPremiseBuildingType.bareShell"
            @change="updateField(['premiseBuildingType'], premiseBuildingType)"
          >
          <span
            v-show="allowEdit ? true : isBareShell"
            :class="{'en-bold': isBareShell}"
          >
            {{ hotbPremiseBuildingType.bareShell | customValueByKey({obj: hotbPremiseBuildingType,rootPath: 'handover' }) }}
          </span>
          <input
            v-show="allowEdit ? true : isOthers"
            v-model="premiseBuildingType"
            type="radio"
            :disabled="!allowEdit"
            :value="hotbPremiseBuildingType.others"
            @change="updateField(['premiseBuildingType'], premiseBuildingType)"
          >
          <span
            v-show="allowEdit ? true : isOthers"
            :class="{'en-bold': isOthers}"
          >
            {{ hotbPremiseBuildingType.others | customValueByKey({obj: hotbPremiseBuildingType,rootPath: 'handover' }) }}
          </span>
        </div>
        <pre v-if="(isTenant || !allowEdit) && premisesRemark">{{ premisesRemark }}</pre>
        <textarea
          v-if="!isTenant && allowEdit"
          v-autosize
          v-fusioninputcheck="{type: 'string', required: allowEdit && isOthers}"
          class="autosize"
          :disabled="!allowEdit"
          :placeholder="premiseBuildingType | customValueByKey({obj: hotbPremiseBuildingType,rootPath: 'handover' })"
          :value="premisesRemark"
          :required="allowEdit && isOthers"
          @change="event => updateField(['premisesRemark'], event.target.value)"
        />
      </div>
      <!-- Special Remarks -->
      <div
        v-show="!isTenant"
        class="special-remark"
      >
        <label>{{ $t('handover.specialRemark') }}</label>
        <TextField
          v-if="!isTenant"
          v-show="allowEdit || (isEdit && allowApprove)"
          :showColon="false"
          :value="currentHotb.remarks"
          @updateValue="val => updateField(['remarks'], val)"
        />
        <pre v-show="!allowEdit && !(isEdit && allowApprove)">{{ currentHotb.remarks || '--' }}</pre>
      </div>
      <!-- Attachments -->
      <div
        v-show="isHKlandUser"
        class="attachments otherDocuments"
      >
        <div
          v-if="isHKlandUser"
          class="title"
          :class="{'required-flag': allowEdit}"
        >
          {{ $t('handover.otherDocuments') }}
        </div>
        <HkldDropzone
          v-if="allowEditDocs"
          :sourceId="hotbId"
        />
        <EditAttachments
          v-if="isHKlandUser"
          :sourceId="hotbId"
          :canEdit="allowEditDocs"
          :filesChanged="filesChanged"
          :canDelete="allowEditDocs"
          :canDownloadAll="false"
          :getFileLengthID="hotbId"
          :updateDescriptionCb="updateDescriptionCb"
        />
        <span v-if="isEmptyAttachments && isWappr">--</span>
      </div>
    </div>
    <div
      id="fillInContactInfoModal"
      class="reveal"
    >
      <InviteContactModal
        :closeModal="closeInviteModal"
        :hkldUserSelectOptions="hkldUserSelectOptions"
        :hkldHotbContacts="hkldHotbContacts"
      />
    </div>
    <Lifecycle
      ref="handoverLifecycle"
      :modalId="'handover-takeback-modules-lifecycle-modal'"
      :type="isHandover ? 'handover' : 'takeback'"
      :commentAttr="lifecycleCommentAttrs.comments"
      :histories="currentHotb.histories || []"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { setTimeout } from 'timers'
import { computed, unref, ref } from 'vue-demi'
import Approval from '@/modules/hotb/Approval'
import DatePicker from '@/controls/DatePicker'
import DialogSlot from '@/controls/DialogSlot'
import EditAttachments from '@/controls/EditAttachments'
import HkldDropzone from '@/controls/HkldDropzone'
import HKLInput from '@/controls/Input/HKLInput'
import Lifecycle from '@/modules/Lifecycle'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import InputField from '@/controls/InputField'
import Sticky from '@/controls/Sticky'
import TextField from '@/controls/TextField'
import formFullnameMixin from '@/pages/formFullnameMixin'
import * as regex from '@/utils/regex'
import EventBus from '@/utils/eventBus'
import dateUtil from '@/utils/dateUtil'
import mutationTypes from '@/store/mutation-types'
import userHelper from '@/utils/userHelper'
import { ActionTypes, EmailTypes, lifecycleCommentAttrs } from '@/utils/consts'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { getAttachments } from '@/api/docs/request'
import { getApproversOfBuilding, getSameBuildingUsers } from '@/api/users/request'
import { hotbAttrTypes, hotbPremiseBuildingType, hotbStatus } from '@/utils/locale'
import { logHotbChange, updateHotbUpdatedBy } from '@/api/hotb/request'
import { Rcs } from '@/utils/aclHelper'
import { utils as formUtils } from '@/plugin/InputChange'
import { sendEmail } from '@/api/postman/request'
import { concatLeasesAsPremises } from '@/modules/ccd/tenancy/utils'
import { useStoreGetters } from '@/store/index'
import { getHotbCompanyName, getHotbTradeName, updateCompanyNameAndTradeNameIfEmpty } from './utils'
import InviteContactModal from './InviteContactModal'
import SubmitHandover from './SubmitHandover.vue'

export default {
  components: {
    HKLInput,
    HkldDropzone,
    EditAttachments,
    DatePicker,
    TextField,
    InputField,
    DialogSlot,
    Approval,
    InviteContactModal,
    SubmitHandover,
    Lifecycle,
    Sticky,
    HKLMultiSelect,
  },
  mixins: [formFullnameMixin],
  props: {
    allowEdit: {
      type: Boolean,
      default: false,
    },
    allowApprove: {
      type: Boolean,
      default: false,
    },
    allowInvite: {
      type: Boolean,
      default: false,
    },
    allowEditDocs: {
      type: Boolean,
      default: false,
    },
    isWappr: {
      type: Boolean,
      default: false,
    },
    isSpecial: {
      type: Boolean,
      default: false,
    },
    tabsContainerIdMap: {
      type: Object,
      default: () => ({}),
    },
  },
  setup () {
    const { isTenant, currentUser, isHKlandUser } = useStoreGetters('user', ['isTenant', 'currentUser', 'isHKlandUser'])
    const highlightInvalidDate = ref(false)
    const requestChangeComments = ref('')
    const beforeCloseRequestChangeModal = () => {
      requestChangeComments.value = ''
      formUtils.resetDirty()
    }
    return {
      isHKlandUser,
      isTenant,
      highlightInvalidDate,
      isTenantClass: computed(() => {
        return { 'isTenant': unref(isTenant) }
      }),
      invalidDateErrHighlightCls: computed(() => {
        return { 'error-highlight': unref(highlightInvalidDate) }
      }),
      currentUser,
      requestChangeComments,
      beforeCloseRequestChangeModal,
    }
  },
  data () {
    const toDate = new Date(Date.now())
    toDate.setDate(toDate.getDate() - 1)
    const disabledDates = {
      to: toDate,
    }
    return (
      {
        attachment: {
          description: '',
          files: [],
        },
        Rcs,
        isEdit: false,
        remarks: '',
        transactionDate: '',
        hotbPremiseBuildingType,
        premiseBuildingType: hotbPremiseBuildingType.asIs,
        dateUtil,
        userHelper,
        deep,
        disabledDates,
        regex,
        fillInContactInfoModal: '',
        hkldUserSelectOptions: [],
        isEmptyAttachments: false,
        isEmptyAttachmentsForTenant: false,
        approvers: [],
        hkldHotbContacts: [],
        remarkToTenant: '',
        scheduledTransactionDate: '',
        lifecycleCommentAttrs,
        companyNameKey: 'hotb_company_name_1',
        tradeNameKey: 'hotb_trade_name_1',
        premisesNameKey: 'hotb_premises_name_1',

      }
    )
  },

  computed: {
    ...mapGetters('hotb', ['currentHotb', 'isHandover']),
    ...mapGetters('form', ['isPageDirty']),
    buildingId () {
      return lodash.get(this.tenancy, 'building.id')
    },
    isAsIs () {
      return this.premiseBuildingType === hotbPremiseBuildingType.asIs
    },
    isBareShell () {
      return this.premiseBuildingType === hotbPremiseBuildingType.bareShell
    },
    isOthers () {
      return this.premiseBuildingType === hotbPremiseBuildingType.others
    },
    tenancyType () {
      return lodash.get(this.currentHotb, 'tenancy.type')
    },
    hotbType () {
      return hotbAttrTypes[this.currentHotb.type]
    },
    saveButtonClass () {
      return this.isWappr ? 'save btn--orange' : 'save btn--darkblue'
    },
    isAppr () {
      return hotbStatus.APPR === deep(this.currentHotb, 'status')
    },
    isScheduled () {
      return hotbStatus.SCHEDULED === deep(this.currentHotb, 'status')
    },
    isAfterScheduled () {
      return [hotbStatus.SCHEDULED, hotbStatus.UNSIGNED, hotbStatus.COMPLETED].includes(deep(this.currentHotb, 'status'))
    },
    isRejected () {
      return hotbStatus.REJECTED === deep(this.currentHotb, 'status')
    },
    isCompleted () {
      return hotbStatus.COMPLETED === deep(this.currentHotb, 'status')
    },
    allowEditDate () {
      return this.allowEdit
    },
    hotbId () {
      const id = this.currentHotb.id || ''
      if (id) {
        this.$on(`controls.hkldDropzone.loadFilesLength${id}`, (filesCount) => {
          this.isEmptyAttachments = !filesCount
        })
      }
      return id
    },
    lastUpdatedBy () {
      return this.getDisplayName(deep(this.currentHotb, 'updator'))
    },
    pubDocResourceId () {
      const id = lodash.get(this.currentHotb, 'pubDocResourceId', '')
      if (id) {
        this.$on(`controls.hkldDropzone.loadFilesLength${id}`, (filesCount) => {
          this.isEmptyAttachmentsForTenant = !filesCount
        })
      }
      return id
    },
    floor: {
      get () {
        return lodash.cloneDeep(lodash.get(this.currentHotb, 'floor', []))
      },
      set (val) {
        this.floor = val
        return this.floor
      },
    },
    tenancy () {
      const tenancy = lodash.get(this.currentHotb, 'tenancy', {})
      // TODO @sam_liang@epam.com should remove set premises when get tenancy.
      // hotb.premises should be set and save when not edit but save or submit
      const tenancyPremises = concatLeasesAsPremises(tenancy)
      if (tenancy && tenancyPremises && !this.currentHotb.premises) {
        this.updateField(['premises'], tenancyPremises, false)
      }
      return tenancy
    },
    companyName () {
      return lodash.get(this.currentHotb, 'companyName') || getHotbCompanyName(this.currentHotb) || this.$t('emptyValue')
    },

    tradeName () {
      return lodash.get(this.currentHotb, 'tradeName') || getHotbTradeName(this.currentHotb) || this.$t('emptyValue')
    },

    premisesRemark () {
      return deep(this.currentHotb, 'premisesRemark') || ''
    },

    contacts () {
      const contacts = deep(this.currentHotb, 'contacts') || []
      if (contacts.length === 0) return {}
      return contacts.reduce((partialData, contact) => {
        return {
          ...partialData,
          [contact.type.toLowerCase()]: contact.value,
        }
      }, {})
    },
    approvalHistories () {
      const histories = deep(this.currentHotb, 'histories') || []
      if (histories.length === 0) return []
      return histories.map(history => {
        return {
          approver: deep(history, 'who.displayName') || deep(history, 'who.username'),
          comments: deep(history, 'detail.comments'),
          updateTime: dateUtil.format(history.updatedAt, 'DD MMM YY HH:mm:ss'),
          status: deep(history, 'status'),
        }
      })
    },
    isDraft () {
      return [hotbStatus.DRAFT, hotbStatus.REJECTED].includes(this.currentHotb.status)
    },
    floorOptions () {
      const premisesDetailList = lodash.get(this.currentHotb, 'tenancy.premisesDetailList', [])
      if (lodash.isEmpty(premisesDetailList)) {
        return []
      } else {
        return lodash.uniq(premisesDetailList.map(item => item.floor))
      }
    },
    floorHklOptions () {
      return (this.floorOptions || []).map(floor => ({
        name: floor,
        value: floor,
      }))
    },
  },

  watch: {
    currentHotb () {
      this.premiseBuildingType = this.currentHotb.premiseBuildingType
      this.getHkldContact()
    },
    async buildingId () {
      await this.loadOptions()
    },
    async allowEditDate () {
      await this.getApproversOption()
    },
  },

  async mounted () {
    await this.loadOptions()

    this.premiseBuildingType = this.currentHotb.premiseBuildingType
    this.getHkldContact()
    EventBus.$on('modules-handovers-submit-disable-edit', () => {
      setTimeout(this.disableEdit, 100)
    })
  },

  destroyed () {
    if (this.fillInContactInfoModal) {
      this.fillInContactInfoModal.destroy()
    }
    $('#fillInContactInfoModal').remove()
  },

  methods: {
    ...mapActions('hotb', ['loadHotb', 'updateHotb', 'patchStatus']),
    ...mapMutations('hotb', {
      updateFieldByPath: mutationTypes.HOTB_UPDATE_FIELD_PATH,
      setCurrentHotb: mutationTypes.HOTB_SET_CURRENT,
    }),
    openLifecycleModal () {
      this.$refs.handoverLifecycle.init()
    },
    enableEdit () {
      this.isEdit = true
      this.transactionDate = this.currentHotb.transactionDate
      this.remarks = this.currentHotb.remarks
    },

    discardChanges () {
      this.updateField(['transactionDate'], this.transactionDate)
      this.updateField(['remarks'], this.remarks)
      formUtils.resetDirty()
      this.isEdit = false
    },
    async changeUpdator (updateHotbUpdatedByOnly = false) {
      if (this.currentHotb.status === hotbStatus.APPR || this.currentHotb.status === hotbStatus.WAPPR) {
        this.updateFieldByPath({
          path: ['updatedBy'],
          newValue: this.currentUser.id,
        })
        this.updateFieldByPath({
          path: ['updatedAt'],
          newValue: new Date(),
        })
        const newHotb = updateHotbUpdatedByOnly ? await updateHotbUpdatedBy(this.currentHotb.id, {
          updatedBy: this.currentUser.id}) : await this.updateHotb({attrs: ['updatedBy']})
        this.updateFieldByPath({
          path: ['updator'],
          newValue: newHotb.updator,
        })
      }
    },
    async filesChanged (files) {
      this.isEmptyAttachments = !files.length
      if (files.length) {
        this.removeErrorOfEmptyDocuments()
      }
      await this.changeUpdator(true)
    },
    async tenantFilesChanged (files) {
      this.isEmptyAttachmentsForTenant = !files.length
      await this.changeUpdator()
    },
    getName () {
      const name = this.contacts && this.contacts.name
      return name ? this.displayI18nFullName(name.firstName, name.lastName) : ''
    },

    getDisplayName (user = {}) {
      return userHelper.getUserName(user.username, user.displayName)
    },

    getContactValue (contacts, attr) {
      const value = deep(contacts, attr)
      return this.allowEdit ? value : (value || '--')
    },

    getAMRemarks () {
      let comments = ''
      if (this.approvalHistories.length > 1) {
        let latestCommentIndex = -1
        let latestCommentDate = null
        this.approvalHistories.forEach((history, index) => {
          if (history.status === 'APPR' || history.status === 'REJECTED') {
            if (latestCommentDate === null) {
              latestCommentDate = history.updateTime
              latestCommentIndex = index
            } else if (history.updateTime > latestCommentDate) {
              latestCommentDate = history.updateTime
              latestCommentIndex = index
            }
          }
        })
        if (latestCommentIndex > -1) {
          comments = `${this.approvalHistories[latestCommentIndex].comments}`
        }
      }
      return comments || ''
    },

    async showInviteModal () {
      await this.initModal()
      this.fillInContactInfoModal.open()
      setTimeout(formUtils.resizeElements, 0)
    },

    async closeInviteModal () {
      this.fillInContactInfoModal.close()
      formUtils.resetDirty()
      await this.loadHotb(this.currentHotb.id)
      this.getHkldContact()
    },

    async initModal () {
      if (!this.fillInContactInfoModal) {
        this.fillInContactInfoModal = await new Foundation.Reveal($('#fillInContactInfoModal'), {
          closeOnEsc: false,
          closeOnClick: false,
        })
      }
    },
    closeModal () {
      this.$refs.requestChange.closeModal()
    },
    getHkldContact () {
      this.hkldHotbContacts = deep(this.currentHotb, 'hkldHotbContacts') || []
      this.remarkToTenant = lodash.get(this.currentHotb, 'remarkToTenant', '')
      this.scheduledTransactionDate = lodash.get(this.currentHotb, 'scheduledTransactionDate', '')
    },
    updateField (path, newValue, setDirty = true) {
      this.updateFieldByPath({ path, newValue })
      setDirty && formUtils.setDirty()
    },
    updateCompanyName (event) {
      this.updateField(['companyName'], event.target.value)
      this.companyNameKey += 1
    },
    updateTradeName (event) {
      this.updateField(['tradeName'], event.target.value)
      this.tradeNameKey += 1
    },
    updatePremises (value) {
      this.updateField(['premises'], value)
      this.premisesNameKey += 1
    },
    updateContact (path = [], newValue) {
      const type = path[0]
      const contactTypes = Object.keys(this.contacts)
      if (!contactTypes.length || !contactTypes.includes(type)) {
        this.updateField(
          ['contacts', contactTypes.length],
          {
            sourceId: this.currentHotb.id,
            description: type,
            priority: true,
            type: type.toUpperCase(),
            value: path.length > 1 ? { [path[1]]: newValue } : newValue,
            valueType: path.length > 1 ? 'JSON' : 'STRING',
          },
        )
        return
      }
      this.currentHotb.contacts.forEach((contact, index) => {
        if (contact.type.toLowerCase() === type) {
          let fieldPath = ['contacts', index, 'value']
          if (path.length > 1 && contact.valueType === 'JSON') {
            fieldPath.push(path[1])
          }
          this.updateField(fieldPath, newValue)
        }
      })
    },
    resetValidation () {
      fusionInputCheckUtil.resetAllFields()
      this.highlightInvalidDate = false
      $('.floor .hkl-select').removeClass('error-highlight')
    },

    formatDate (val) {
      return dateUtil.format(val, 'DD MMM YY HH:mm')
    },

    async validate () {
      if (!deep(this.currentHotb, 'transactionDate')) {
        this.highlightInvalidDate = true
      }

      if (lodash.isEmpty(this.floor)) {
        $('.floor .hkl-select').addClass('error-highlight')
      }

      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.handover-information')
      if (this.hotbId) {
        this.otherDocuments = await getAttachments(this.hotbId)
        if (lodash.isEmpty(this.otherDocuments)) {
          this.showErrorOfEmptyDocuments()
          return false
        } else {
          this.removeErrorOfEmptyDocuments()
        }
      } else {
        this.showErrorOfEmptyDocuments()
        return false
      }
      return !this.highlightInvalidDate && $('.handover-information input.is-invalid-input').length === 0 &&
        fusionInputCheckUtil.isAllFieldsValid('.handover-information') && !lodash.isEmpty(this.floor)
    },

    showErrorOfEmptyDocuments () {
      $('.otherDocuments').addClass('error-highlight')
      $('.otherDocuments .vue-dropzone').addClass('error-highlight')
    },

    removeErrorOfEmptyDocuments () {
      $('.otherDocuments').removeClass('error-highlight')
      $('.otherDocuments .vue-dropzone').removeClass('error-highlight')
    },
    async save () {
      this.resetValidation()
      updateCompanyNameAndTradeNameIfEmpty()
      await this.updateHotb()
      await this.loadHotb(this.currentHotb.id)
      formUtils.resetDirty()
      this.isEdit = false
    },
    async updateStatus (status, comments, action) {
      await this.patchStatus({ request: { status, comments }, action })
      await this.loadHotb(this.currentHotb.id)
      formUtils.resetDirty()
    },
    async reject (comments) {
      await this.updateStatus(hotbStatus.REJECTED, comments, ActionTypes.hotb.reject)
    },
    async approve () {
      await this.updateStatus(hotbStatus.APPR, '', ActionTypes.hotb.approve)
    },
    async getApproversOption () {
      const approvers = await getApproversOfBuilding(this.buildingId, Rcs.Hotb, this.tenancy.id, this.tenancyType)
      this.approvers = lodash.cloneDeep(approvers)
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    async loadOptions () {
      if (this.buildingId) {
        if (this.allowEditDate) {
          await this.getApproversOption()
        }
        this.hkldUserSelectOptions = await getSameBuildingUsers(this.buildingId, this.tenancyType, true)
      }
    },
    disableEdit () {
      this.isEdit = false
    },
    getHkldUserInfo (item, type) {
      let value = ''
      item.contacts.map(contact => {
        if (contact.type === type) {
          value = contact.value
        }
      })
      return value
    },
    showFloors () {
      return this.floor.join(', ')
    },
    onFloorChange (val) {
      this.updateField(['floor'], val || [])
    },
    async requestChange () {
      fusionInputCheckUtil.validateAllFields(undefined, '.request-change-comment')
      if (!this.requestChangeComments ||
        !fusionInputCheckUtil.isAllFieldsValid('.request-change-comment')) return false

      await sendEmail(EmailTypes[this.hotbType].requestChange, { comments: this.requestChangeComments })
      await logHotbChange(
        this.currentHotb.id,
        {
          comments: this.requestChangeComments,
        },
        ActionTypes.hotb.requestChange)

      this.$refs.fillRequestChange.closeModal()
      return true
    },
    async updateDescriptionCb (params) {
      await this.changeUpdator(true)
    },
    async tenantUpdateDescriptionCb (params) {
      await this.changeUpdator()
    },
  },
}
</script>

<style lang='scss'>
@import '~styles/textField';
@import '~styles/multi-value';

#fillInContactInfoModal {
  width: 800px;
  padding: 30px;
}

.handover-information {
  position: relative;
  .input-field label,
  div.text-field label,
  label {
    padding: 0;
    margin: 0;
  }

  div.text-input textarea {
    margin: 0;
    padding: 10px 0 0 10px;
    width: 100%;
  }
  .input-field .text-input {
    input {
      margin: 0;
      padding: 0 0 0 10px;
      width: 100%;
    }
  }
  .en-bold {
    font-weight: bold;
  }

  .normal-title {
    height: 20px;
    font-family: Verdana;
    font-size: 14px;
    font-weight: bold;
    line-height: 1.43;
    text-align: left;
    color: #0c3a58;
  }

  input:disabled {
   border-radius: 50%;
  }
  .error-highlight {
    background-color: #f9dcda !important;
    background: #f9dcda !important;
  }
  .input-field {
    label {
      margin-bottom: 0;
      height: 15px;
    }
    .text-input,
    span {
      display: inline-block;
      width: 100%;
    }
  }
  input {
    color: #0C3A58;
    font-size: 14px;
  }
  .width100 {
    width: 100%;
  }
  .boldFont {
    font-family: Verdana;
    font-weight: bold;
    font-size: 14px;
    color: #083E62;
  }
  .generalFont {
    font-family: Verdana;
    font-size: 14px;
    color: #0C3A58;
  }
  .basicInfo {
    background: #F2F6F8;
    display: flex;
    width: 100%;
    margin-bottom: 15px;
    .basicItem {
      flex: 1;
      flex-direction: column;
      padding: 10px 15px;
      overflow: hidden;
      div {
        flex: 1;
      }
      .title {
        @extend .boldFont;
      }
      .value {
        font-family: Verdana;
        font-size: 16px;
        color: #083E62;
      }
    }
    .building {
        flex-grow: 0.3;
    }
    .status {
      flex-grow: 0.5;
    }
    .status-text {
      cursor: pointer;
      text-decoration: underline;
    }
    .name {
      flex-grow: 4.8;
    }
    .lastUpdateOnandBy {
      flex-grow: 0.7;
    }
  }

  .premisesRemark {
    textarea {
      font-family: Verdana;
      font-size: 14px;
      line-height: 1.43;
      text-align: left;
      color: #0C3A58;
      box-shadow: none;
    }
    div>input[type="radio"] {
      width: 16px;
      height: 16px;
      margin-bottom: 8px;
    }
    .radio{
      span {
        margin-right: 70px;
      }
      span:last-child {
        margin-right: 0;
      }
    }
  }
  .general-info-view {
    .scheduled-handover-date {
      label {
        color: #0c3a58;
        font-weight: bold;
        margin-bottom: 5px;
      }
      margin-bottom: 15px;
    }
    .handoverLastUpdateOn,
    .tenancyBuilding,
    .date,
    .permisesName,
    .premisesRemark,
    .contact-info,
    .special-remark,
    .am-remark,
    .conditionOfPremises {
      margin-bottom: 15px;
      & > div.title,
      label {
        @extend .normal-title;
        margin-bottom: 5px;
      }
    }
    .permisesName p{
      margin: 0;
      line-height: unset;
    }
    .remark-to-tenant {
      label {
        font-weight: bold;
        color: #0C3A58;
      }
    }

    .permisesName {
      align-items: flex-start;
      .text-field:first-child {
        margin-right: 50px;
      }
      .text-field {
        max-width: 750px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .contact-info {
      .handover-contact,
      .phone-number,
      .email {
        max-width: 300px;
        margin-right: 56px;
      }
    }
    .attachments .title {
      margin-bottom: 5px;
    }
  }

  .general-info-edit {
    .handoverLastUpdateOn,
    .tenancyBuilding,
    .permisesName,
    .premisesRemark,
    .contact-info,
    .special-remark,
    .date,
    .am-remark {
      height: 65px;
      margin-bottom: 15px;
      & > div.title,
      label {
       @extend .normal-title;
       margin-bottom: 5px;
      }
    }
    .special-remark {
      textarea {
        min-height: 50px;
      }
    }
    .permisesName,
    .premisesRemark,
    .special-remark {
      height: auto;
    }
    .permisesName {
      align-items: center;
      .text-field {
        width: 520px;
        margin-right: 20px;
      }
    }

    .contact-info.row {
      flex-wrap: nowrap;
    }

    .handover-contact {
      width: 400px;
      margin-right: 20px;
    }
    .phone-number,
    .email{
      width: 300px;
      margin-right: 20px;
    }
    .permisesName {
      align-items: flex-start;
      .text-field {
        display: inline-block;
      }
      .floor {
        min-width: 200px;
        display: inline-block;
        .multiselect__tag {
          height: 38px;
          line-height: 38px;
          vertical-align: bottom;
          background: #6e7a91;
          padding: 0 26px 0 10px;
        }
        i.multiselect__tag-icon::after {
          color: #3a505f;
        }
        i.multiselect__tag-icon:hover{
          background: #969faf;
        }
      }
      .floor label:after {
        color: #E49405;
        content: '*';
      }
    }
  }
  .attachments {
    margin-bottom: 15px;
    div.title {
      @extend .normal-title;
      margin-bottom: 5px;
    }

    attachments-container {
      margin-top: 20px;
      padding-top: 14px;
      padding-bottom: 14px;
      & > .title {
        padding-left: 20px;
      }
    }
  }
  .attach-to-tenant {
    margin-top: 15px;
  }
  .sticky-btn.isTenant {
    .tenant-hotb-request-change {
      position: absolute;
      right: -25px;
    }

    .tenant-handover-request-change-btn {
      pointer-events: auto;
    }
  }

  div.sticky-btn.stick-class {
    height: 50px;
    width: 100%;
    .btnGroup {
      right: 15px;
    }
  }
  .sticky-btn {
    position: absolute;
    top: -153px;
    width: 70%;
    padding: 15px 0;
    text-align: right;
    right: 0;
    pointer-events: none;
  }
  .btnGroup {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 15px;
    position: relative;
    right: -25px;
    pointer-events: auto;

    & > button {
      margin: 0;
      white-space: nowrap;
    }

    .submit-tender {
      height: 40px;
      line-height: 40px;
    }

    .save {
      margin: 0;
    }

    & > div {
      text-align: center;
      line-height: 40px;
      height: 40px;
      font-family: Verdana;
      font-weight: bold;
      font-size: 14px;
      color: #FFF;
    }

    .choose-approver, .reveal {
      display: none;
    }

    .handover-reject {
      margin: 0;
    }

    .approval {
      textarea {
        width: 100% !important;
      }
      .buttons {
        font-size: 0;
        button {
          font-size: 14px;
        }
        button:last-child {
          margin-left: 15px;
        }
      }
    }
  }
  .handover-contacts-info {
    color: #0C3A58;
    font-size: 0;
    position: relative;
    .tenant-handover-request-change-btn {
      vertical-align: bottom;
    }
    .item {
      margin-right: 56px;
      display: inline-block;
      margin-bottom: 15px;
      min-width: 200px;
    }
    .item:nth-child(2) {
      min-width: 130px;
    }
    .item:last-child {
      margin-right: 0;
    }
    label  {
      color: #0C3A58;
      font-weight: bold;
      height: 20px;
      display: block;
      margin-bottom: 5px;
    }
    span {
      height: 20px;
      font-size: 14px;
    }
    .remark {
      display: block;
    }
  }
  #handover-reject,
  #handover-approve {
    float: right;
  }
  .internal-only {
    font-weight: bold;
    margin-bottom: 15px;
    display: block;
  }
}
.choose-approver {
  .form-error {
    margin: 0;
  }
}
</style>
